/**
 * 🖼️ 인라인 이미지 스트립 — 순수 유틸.
 *
 * 서버(main.py)가 모델의 <img-search q="…"/> 태그를 본문에서 `[[image_strip:N]]` 자리표시자로 바꾸고,
 * `image_strip` SSE 이벤트(pending → ok | empty)와 complete.web_images 로 N번째 자리의 이미지를 내려 준다.
 * 이 파일은 그 데이터를 (1) 스트리밍 중 누적하고 (2) 저장용으로 정리하고 (3) 본문을 자리표시자 기준으로 쪼갠다.
 */

export const IMAGE_STRIP_PLACEHOLDER_RE = /\[\[image_strip:(\d+)\]\]/g;

/** 스트립 하나에서 타일로 보여 줄 최대 장수 (나머지는 "+N") */
export const IMAGE_STRIP_VISIBLE_TILES = 3;
/** 이 미만이면 스트립을 그리지 않는다 (서버와 동일 기준) */
export const IMAGE_STRIP_MIN_IMAGES = 2;

const isHttpUrl = (value) => typeof value === 'string' && /^https?:\/\//i.test(value);

/** 이미지 항목 정규화 — 화면에 쓸 수 있는 것만 남긴다 */
export function normalizeStripImages(images) {
  if (!Array.isArray(images)) return [];
  const seen = new Set();
  const out = [];
  for (const raw of images) {
    if (!raw || typeof raw !== 'object') continue;
    const imageUrl = isHttpUrl(raw.image_url) ? raw.image_url : null;
    if (!imageUrl || seen.has(imageUrl)) continue;
    seen.add(imageUrl);
    out.push({
      image_url: imageUrl,
      thumb_url: isHttpUrl(raw.thumb_url) ? raw.thumb_url : imageUrl,
      source_url: isHttpUrl(raw.source_url) ? raw.source_url : '',
      source_domain: typeof raw.source_domain === 'string' ? raw.source_domain : '',
      caption: typeof raw.caption === 'string' ? raw.caption : ''
    });
  }
  return out;
}

/**
 * 스트리밍 중 image_strip 이벤트를 인덱스별로 누적한다.
 * 같은 인덱스는 뒤에 온 이벤트가 덮어쓴다(pending → ok/empty).
 */
export function mergeWebImageStrip(existing, event) {
  const list = Array.isArray(existing) ? existing : [];
  if (!event || !Number.isInteger(event.index) || event.index < 0) return list;
  const status = event.status === 'ok' || event.status === 'pending' ? event.status : 'empty';
  const next = {
    index: event.index,
    query: typeof event.query === 'string' ? event.query : '',
    status,
    images: status === 'ok' ? normalizeStripImages(event.images) : []
  };
  if (next.status === 'ok' && next.images.length < IMAGE_STRIP_MIN_IMAGES) {
    next.status = 'empty';
    next.images = [];
  }
  const filtered = list.filter((item) => item && item.index !== event.index);
  filtered.push(next);
  return filtered.sort((a, b) => a.index - b.index);
}

/**
 * complete 시점의 최종본. 서버 complete.web_images(ok 만) 가 있으면 그것을, 없으면 스트리밍 중 ok 만 남긴다.
 * 저장(Firestore)과 화면 모두 이 결과를 쓴다. status 필드는 저장하지 않는다(ok 만 남으므로).
 */
export function finalizeWebImages(completeWebImages, streamed) {
  const source = Array.isArray(completeWebImages) ? completeWebImages : (Array.isArray(streamed) ? streamed : []);
  const out = [];
  for (const item of source) {
    if (!item || !Number.isInteger(item.index)) continue;
    if (item.status && item.status !== 'ok') continue;
    const images = normalizeStripImages(item.images);
    if (images.length < IMAGE_STRIP_MIN_IMAGES) continue;
    out.push({ index: item.index, query: typeof item.query === 'string' ? item.query : '', images });
  }
  return out.sort((a, b) => a.index - b.index);
}

// 서버(chat_image_strip_service.py)가 사진 줄로 바꾸는 태그 모양과 같다. 구형 백엔드·플래그 off·이미 저장된 답변에서
// 글자로 남은 것은 검색할 수 없으니 화면에서만 지운다. 한국어 모양은 줄 하나 전체일 때만 — `<사진 찾기 놀이>` 같은 글은 둔다.
const TAG_NAME = '(?:img|image)[-_]search';
const KO_NAME = '(?:사진|이미지)\\s*검색어?';
const LEAKED_TAG_RE = new RegExp(
  `<[^<>"'\\n]{0,4}?${TAG_NAME}[\\s\\u200b-\\u200d\\u2060\\ufeff]+q\\s*=\\s*(["'])[^"'<>\\n]{0,120}\\1\\s*\\/?\\s*>`,
  'gi',
);
const LEAKED_KO_LINE_RE = new RegExp(
  `^[ \\t]*<\\s{0,2}(?:[^<>"'\\n:：]{0,8}?\\s)?${KO_NAME}\\s*[:：]\\s*[^<>"'\\n]{1,120}?\\s*\\/?\\s*>[ \\t]*(?:\\n|$)`,
  'gm',
);
const LEAKED_META_LINE_RE = new RegExp(
  `^[ \\t]*<[^<>\\n]{0,12}?(?:${TAG_NAME}|${KO_NAME})\\s*태그[^<>\\n]{0,200}>?[ \\t]*(?:\\n|$)`,
  'gim',
);
const LEAKED_TAG_TAIL_RE = new RegExp(`<[^<>"'\\n]{0,4}${TAG_NAME}[^>]*$`, 'i');
const TAG_HINT_RE = new RegExp(`${TAG_NAME}|${KO_NAME}`, 'i');

/** 글자로 남은 사진 검색 태그(영어·이름 틀린 것·한국어 번역·"태그 불필요" 판단 줄)를 지운다. 자리표시자는 남긴다. */
export function stripLeakedImageTags(content) {
  const text = typeof content === 'string' ? content : '';
  if (!text || !TAG_HINT_RE.test(text)) return text;
  return text
    .replace(LEAKED_TAG_RE, '')
    .replace(LEAKED_KO_LINE_RE, '')
    .replace(LEAKED_META_LINE_RE, '')
    .replace(LEAKED_TAG_TAIL_RE, '')
    .replace(/\n{3,}/g, '\n\n');
}

const PLACEHOLDER_ANY_RE = /\[\[(?:image_strip|rich):\d+\]\]/g;
const FENCE_LINE_RE = /^ {0,3}(`{3,}|~{3,})/;
const isTableLine = (line) => {
  const trimmed = line.trim();
  return trimmed.length > 1 && (trimmed.startsWith('|') || trimmed.endsWith('|'));
};
// 자리표시자를 뺀 뒤 칸이 모두 빈 표 줄(`| | |`)
const EMPTY_TABLE_ROW_RE = /^\s*\|?(?:\s*\|)+\s*$/;
// 자리표시자를 뺀 뒤 목록 기호·강조 기호만 남은 줄
const EMPTY_MARKUP_LINE_RE = /^\s*(?:[-*+]|\d+[.)])?\s*[*_~`]*\s*$/;
const HEADING_LINE_RE = /^ {0,3}#{1,6}\s/;

/**
 * 표 칸·목록 항목·문장 중간에 들어간 자리표시자를 그 블록이 끝난 바로 아래 줄로 옮긴다.
 * 화면은 자리표시자에서 본문을 잘라 그리므로, 표 안에 있으면 표가 끊기고 `|` 가 글자로 남는다(2026-10 운영).
 * 혼자 한 줄인 자리표시자는 두되, 표 줄 사이에 끼어 있으면 표 아래로 옮긴다. 코드 블록 안은 건드리지 않는다.
 */
export function relocateInlinePlaceholders(content) {
  const text = typeof content === 'string' ? content : '';
  if (!text.includes('[[image_strip:') && !text.includes('[[rich:')) return text;
  const lines = text.split('\n');
  const out = [];
  let pending = [];
  let pendingUntil = null; // 'table' | 'paragraph'
  let fence = null;
  const flush = () => {
    if (!pending.length) return;
    while (out.length && !out[out.length - 1].trim()) out.pop();
    out.push('', ...pending, '');
    pending = [];
    pendingUntil = null;
  };
  lines.forEach((line, i) => {
    if (fence) {
      out.push(line);
      const close = line.match(FENCE_LINE_RE);
      if (close && close[1][0] === fence[0] && close[1].length >= fence.length) fence = null;
      return;
    }
    if (pendingUntil === 'table' && !isTableLine(line)) flush();
    if (pendingUntil === 'paragraph' && (!line.trim() || isTableLine(line) || HEADING_LINE_RE.test(line))) flush();
    const open = line.match(FENCE_LINE_RE);
    if (open) {
      flush();
      fence = open[1];
      out.push(line);
      return;
    }
    const found = line.match(PLACEHOLDER_ANY_RE);
    if (!found) {
      if (pendingUntil === 'table' && EMPTY_TABLE_ROW_RE.test(line)) return;
      out.push(line);
      return;
    }
    const rest = line.replace(PLACEHOLDER_ANY_RE, '');
    const prevIsTable = out.length > 0 && isTableLine(out[out.length - 1]);
    const nextIsTable = i + 1 < lines.length && isTableLine(lines[i + 1]);
    if (!rest.trim()) {
      if (prevIsTable && nextIsTable) {
        pending.push(...found);
        pendingUntil = 'table';
      } else {
        flush();
        out.push(line);
      }
      return;
    }
    const inTable = isTableLine(line);
    pending.push(...found);
    if (!pendingUntil) pendingUntil = inTable ? 'table' : 'paragraph';
    const emptied = inTable ? EMPTY_TABLE_ROW_RE.test(rest) : EMPTY_MARKUP_LINE_RE.test(rest);
    if (!emptied) out.push(inTable ? rest : rest.replace(/[ \t]+$/, ''));
  });
  flush();
  return out.join('\n');
}

/**
 * 본문을 자리표시자 기준으로 쪼갠다.
 *   → [{type:'text', text}, {type:'strip', index}, {type:'cards', index}, …]
 * 사진 스트립 [[image_strip:N]] 과 추천 카드 [[rich:N]] 을 함께 다룬다.
 * 자리표시자가 없으면 [{type:'text', text: content}] 하나.
 */
export function splitContentByImageStrips(content) {
  const text = relocateInlinePlaceholders(stripLeakedImageTags(content));
  if (!text.includes('[[image_strip:') && !text.includes('[[rich:')) return [{ type: 'text', text }];
  const parts = [];
  let last = 0;
  const re = /\[\[(image_strip|rich):(\d+)\]\]/g;
  let match;
  while ((match = re.exec(text)) !== null) {
    const before = text.slice(last, match.index);
    if (before.trim()) parts.push({ type: 'text', text: before });
    parts.push({ type: match[1] === 'rich' ? 'cards' : 'strip', index: Number(match[2]) });
    last = match.index + match[0].length;
  }
  const tail = text.slice(last);
  if (tail.trim()) parts.push({ type: 'text', text: tail });
  return parts.length > 0 ? parts : [{ type: 'text', text: '' }];
}

/** 복사·공유·읽기용 — 자리표시자를 지운 본문 */
export function stripImageStripPlaceholders(content) {
  const text = relocateInlinePlaceholders(stripLeakedImageTags(content));
  if (!text.includes('[[image_strip:')) return text;
  return text.replace(IMAGE_STRIP_PLACEHOLDER_RE, '').replace(/\n{3,}/g, '\n\n');
}

/** 인덱스로 스트립 찾기 (web_images 는 index 순 정렬이지만 빠질 수 있으므로 검색) */
export function findStripByIndex(webImages, index) {
  if (!Array.isArray(webImages)) return null;
  return webImages.find((item) => item && item.index === index) || null;
}
